import { t as SystemStatus } from "./status-ChiHYKYc.mjs";
import { n as DEFAULT_CONFIG, r as LogLevel, t as getLogger } from "./logger-CpoV6RQj.mjs";
import { n as WINDOW, t as getErrorManager } from "./errorManager-CoSVVhi4.mjs";

//#region src/types/stateMachine.ts
let WidgetState = /* @__PURE__ */ function(WidgetState$1) {
	WidgetState$1["VISIBLE"] = "VISIBLE";
	WidgetState$1["CLOSED"] = "CLOSED";
	WidgetState$1["IDLE"] = "IDLE";
	return WidgetState$1;
}({});
let EventType = /* @__PURE__ */ function(EventType$1) {
	EventType$1["STATE_CHANGED"] = "stateChanged";
	EventType$1["STATUS_CHECK_REQUESTED"] = "statusCheckRequested";
	EventType$1["STATUS_CHECK_COMPLETED"] = "statusCheckCompleted";
	EventType$1["SHOW_ALERT"] = "showAlert";
	EventType$1["HIDE_ALERT"] = "hideAlert";
	EventType$1["CLOSE_ALERT"] = "closeAlert";
	return EventType$1;
}({});

//#endregion
//#region src/utils/localStorage.ts
/**
* Utility for interacting with localStorage with error handling and prefixing
*/
const DEFAULT_PREFIX = "oops-widget-";
const logger$1 = getLogger();
/**
* Get item from localStorage with prefix
* @param key Storage key
* @param prefix Prefix to use for the key
* @returns The stored value or null if not found
*/
function getItem(key, prefix = DEFAULT_PREFIX) {
	try {
		const fullKey = `${prefix}${key}`;
		const value = localStorage.getItem(fullKey);
		if (!value) return null;
		return JSON.parse(value);
	} catch (error) {
		logger$1.warn(`Error getting localStorage item ${key}:`, error);
		return null;
	}
}
/**
* Set item in localStorage with prefix
* @param key Storage key
* @param value Value to store
* @param prefix Prefix to use for the key
* @returns Whether the operation was successful
*/
function setItem(key, value, prefix = DEFAULT_PREFIX) {
	try {
		const fullKey = `${prefix}${key}`;
		localStorage.setItem(fullKey, JSON.stringify(value));
		return true;
	} catch (error) {
		logger$1.warn(`Error setting localStorage item ${key}:`, error);
		return false;
	}
}
/**
* Remove item from localStorage with prefix
* @param key Storage key
* @param prefix Prefix to use for the key
* @returns Whether the operation was successful
*/
function removeItem(key, prefix = DEFAULT_PREFIX) {
	try {
		const fullKey = `${prefix}${key}`;
		localStorage.setItem(fullKey, "");
		localStorage.removeItem(fullKey);
		return true;
	} catch (error) {
		logger$1.warn(`Error removing localStorage item ${key}:`, error);
		return false;
	}
}

//#endregion
//#region src/utils/alertStorage.ts
const logger = getLogger();
/**
* Keys for alert storage
*/
var StorageKey = /* @__PURE__ */ function(StorageKey$1) {
	StorageKey$1["LAST_CLOSED_ALERT"] = "closed-alerts";
	return StorageKey$1;
}(StorageKey || {});
/**
* Record that an alert has been closed by the user
*/
function recordClosedAlert(status) {
	logger.debug(`Recording closed alert for status: ${status}`);
	const data = {
		status,
		closedAt: Date.now()
	};
	if (!setItem(StorageKey.LAST_CLOSED_ALERT, data)) logger.warn(`Failed to store closed alert for status: ${status}`);
}
/**
* Get the last closed alert data
*/
function getClosedAlertData() {
	const data = getItem(StorageKey.LAST_CLOSED_ALERT);
	logger.debug(`Retrieved closed alert data: ${data ? `status=${data.status}, closedAt=${new Date(data.closedAt).toISOString()}` : "none"}`);
	return data;
}
/**
* Check if an alert with the given status should be shown, based on closure settings
*
* @param status Current system status
* @param closeDurationMs How long to suppress alerts after closing (0 = forever until status changes)
* @returns Whether the alert should be shown
*/
function shouldShowAlert(status, closeDurationMs) {
	logger.debug(`Checking if alert should be shown for status: ${status}, closeDurationMs: ${closeDurationMs}`);
	const closedData = getClosedAlertData();
	if (!closedData) {
		logger.debug("No closed alert data found, showing alert");
		return true;
	}
	if (closedData.status !== status) {
		logger.debug(`Status changed from ${closedData.status} to ${status} since last closure, showing alert`);
		return true;
	}
	if (closeDurationMs === 0) {
		logger.debug(`Alert closed indefinitely for status ${status}, not showing`);
		return false;
	}
	const elapsedMs = Date.now() - closedData.closedAt;
	const shouldShow = elapsedMs >= closeDurationMs;
	logger.debug(`Time since last closed (${status}): ${elapsedMs}ms, threshold: ${closeDurationMs}ms, showing: ${shouldShow}`);
	return shouldShow;
}
/**
* Clear all stored alert data
*/
function clearAlertStorage() {
	logger.debug("Clearing all alert storage data");
	if (!removeItem(StorageKey.LAST_CLOSED_ALERT)) logger.warn("Failed to clear some alert storage data");
}

//#endregion
//#region src/utils/configUtils.ts
const MIN_CHECK_INTERVAL_MS = 3e4;
const DEFAULT_CHECK_INTERVAL_MS = 6e4;
const DEFAULT_CLOSE_DURATION_MS = 3e5;
/**
* Gets the configured close duration for a given status, applying defaults.
* @param config The widget configuration object.
* @param status The system status.
* @returns The close duration in milliseconds.
*/
function getCloseDurationMs(config, status) {
	return (config.alertClosureBehavior?.[status])?.closeDurationMs ?? DEFAULT_CLOSE_DURATION_MS;
}
/**
* Gets the configured check interval for a given status, applying defaults and enforcing a minimum.
* @param config The widget configuration object.
* @param status The system status.
* @param logger A logger instance for warnings.
* @returns The validated check interval in milliseconds.
*/
function getValidatedCheckIntervalMs(config, status, logger$2) {
	const configuredInterval = (config.alertClosureBehavior?.[status])?.checkIntervalMs ?? DEFAULT_CHECK_INTERVAL_MS;
	if (configuredInterval === 0) return 0;
	if (configuredInterval < MIN_CHECK_INTERVAL_MS) {
		logger$2.warn(`Configured checkIntervalMs (${configuredInterval}ms) for status ${status} is below the minimum (${MIN_CHECK_INTERVAL_MS}ms) and not 0. Using minimum interval instead.`);
		return MIN_CHECK_INTERVAL_MS;
	}
	return configuredInterval;
}

//#endregion
//#region src/utils/createSandboxedFetch.ts
let cleanFetchInstance = null;
let cleanFetchIframe = null;
/**
* Returns an unmodified instance of fetch from a sandboxed iframe,
* so that any interceptors applied to WINDOW.fetch are bypassed.
* The iframe is kept in the DOM to prevent its global scope from shutting down.
*
* This implementation ensures complete isolation from any interceptors
* like those defined in fetchInterceptor.ts and xhrInterceptor.ts.
*
* @returns The original fetch function.
*/
function createSandboxedFetch() {
	if (cleanFetchInstance) return cleanFetchInstance;
	const doc = WINDOW.document;
	if (doc && typeof doc.createElement === "function") try {
		cleanFetchIframe = doc.createElement("iframe");
		cleanFetchIframe.hidden = true;
		cleanFetchIframe.src = "about:blank";
		doc.head.appendChild(cleanFetchIframe);
		const contentWindow = cleanFetchIframe.contentWindow;
		if (contentWindow && typeof contentWindow.fetch === "function") {
			cleanFetchInstance = contentWindow.fetch.bind(contentWindow);
			return cleanFetchInstance;
		}
	} catch {
		if (cleanFetchIframe && cleanFetchIframe.parentNode) {
			cleanFetchIframe.parentNode.removeChild(cleanFetchIframe);
			cleanFetchIframe = null;
		}
	}
	throw new Error("Failed to create an isolated fetch instance. This is required for the application to function correctly.");
}

//#endregion
//#region src/utils/urlParams.ts
/**
* Parse URL parameters from a string, supporting array parameters with [] notation
* @param text URL parameters as a string
* @returns Parsed parameters as a Record with support for array values
*/
function parseAdvancedUrlParams(text) {
	const cleanedText = text.replace(/[\r\n]+/g, "");
	const params = new URLSearchParams(cleanedText);
	const result = Object.create(null);
	const arrayParams = {};
	for (const [key, value] of params.entries()) {
		const arrayMatch = key.match(/^(.+)\[\]$/);
		if (arrayMatch) {
			const baseKey = arrayMatch[1];
			if (!arrayParams[baseKey]) arrayParams[baseKey] = [];
			arrayParams[baseKey].push(value);
		} else result[key] = value;
	}
	Object.entries(arrayParams).forEach(([key, values]) => {
		result[key] = values;
	});
	return result;
}

//#endregion
//#region src/managers/alertManager.ts
var AlertManager = class {
	deps;
	alertElement = null;
	boundInternalAlertClosedHandler;
	constructor(dependencies) {
		this.deps = dependencies;
		this.boundInternalAlertClosedHandler = this._internalAlertClosedHandler.bind(this);
	}
	get logger() {
		return this.deps.logger;
	}
	get config() {
		return this.deps.config;
	}
	get i18nProvider() {
		return this.deps.i18nProvider;
	}
	async getStatusDetails(status) {
		try {
			if (!this.i18nProvider) {
				this.logger.error("AlertManager: i18nProvider is not available.");
				return {
					title: status,
					message: "Status details unavailable."
				};
			}
			return await this.i18nProvider.getStatusDetails(status);
		} catch (error) {
			this.logger.error("AlertManager: Error getting status details from i18n provider:", error);
			return {
				title: status,
				message: "Could not load status details."
			};
		}
	}
	_internalAlertClosedHandler() {
		this.logger.debug("AlertManager: User closed alert, notifying main widget.");
		this.deps.notifyAlertClosedFn();
	}
	/**
	* Called by OopsWidget when the TimerManager signals its onTimerStart callback.
	* This will dispatch the 'oops-widget-timer-start' event on the alert element.
	* @param duration The duration of the timer.
	*/
	notifyTimerStarted(duration) {
		if (this.alertElement) {
			this.logger.debug(`AlertManager: Timer externally started/restarted, dispatching event with duration: ${duration}ms`);
			this.alertElement.dispatchEvent(new CustomEvent("oops-widget-timer-start", { detail: { duration } }));
		}
	}
	/**
	* Called by OopsWidget when the TimerManager signals its onTimerCancel callback.
	* This will dispatch the 'oops-widget-timer-cancel' event on the alert element.
	*/
	notifyTimerCancelled() {
		if (this.alertElement) {
			this.logger.debug("AlertManager: Timer externally cancelled, dispatching event.");
			this.alertElement.dispatchEvent(new CustomEvent("oops-widget-timer-cancel"));
		}
	}
	async showAlert(status, statusResponse) {
		if (status === SystemStatus.Operational) {
			this.logger.debug(`AlertManager.showAlert called with status ${status}, hiding any existing alert.`);
			this.hideAlert();
			return;
		}
		this.logger.debug(`AlertManager.showAlert: Attempting to show/update alert for status: ${status}`);
		if (!shouldShowAlert(status, getCloseDurationMs(this.config, status))) {
			this.logger.debug(`AlertManager: Alert for status ${status} was recently closed, skipping show.`);
			return;
		}
		if (this.alertElement) {
			if (await this.updateExistingAlertElement(status, statusResponse)) return;
			this.hideAlert();
		}
		await this.createNewAlertElement(status, statusResponse);
	}
	async updateExistingAlertElement(status, statusResponse) {
		if (!this.alertElement) return false;
		const currentStatusOnElement = this.alertElement.getAttribute("status-type");
		if (currentStatusOnElement === status) {
			this.logger.debug("AlertManager.updateExistingAlertElement: Alert already shown for %s. Updating timer progress.", status);
			this.updateAlertTimerProgress(this.alertElement);
			return true;
		}
		this.logger.debug("AlertManager.updateExistingAlertElement: Alert exists for %s, attempting update to %s.", currentStatusOnElement, status);
		try {
			const responseForConfigure = statusResponse ?? await this.deps.internalCheckStatusFn();
			if (responseForConfigure.status !== status) {
				this.logger.warn("AlertManager.updateExistingAlertElement: Target status was %s, but check returned %s. Aborting update.", status, responseForConfigure.status);
				return false;
			}
			await this.configureAlertElement(this.alertElement, status, responseForConfigure);
			this.logger.debug("AlertManager.updateExistingAlertElement: Successfully updated existing alert to %s.", status);
			return true;
		} catch (updateError) {
			this.logger.error("AlertManager.updateExistingAlertElement: Failed to update existing alert element. Will recreate.", updateError);
			return false;
		}
	}
	async createNewAlertElement(status, statusResponse) {
		this.logger.debug(`AlertManager.createNewAlertElement: Creating new alert element for ${status}.`);
		try {
			const alertTagName = "oops-widget";
			if (!this.config.alertComponent) {
				this.logger.error("AlertManager: Cannot show alert: alertComponent is not configured.");
				return;
			}
			const newElement = document.createElement(alertTagName);
			const responseForConfigure = statusResponse ?? await this.deps.internalCheckStatusFn();
			if (responseForConfigure.status !== status) {
				this.logger.warn("AlertManager.createNewAlertElement: Target status was %s, but check returned %s. Aborting show.", status, responseForConfigure.status);
				if (responseForConfigure.status === SystemStatus.Operational) this.hideAlert();
				return;
			}
			if (status !== SystemStatus.Operational && responseForConfigure.status === SystemStatus.Operational) {
				this.logger.warn("AlertManager.createNewAlertElement: Target status %s is non-operational, but fresh check returned Operational. Aborting show.", status);
				this.hideAlert();
				return;
			}
			await this.configureAlertElement(newElement, status, responseForConfigure);
			this.alertElement = newElement;
			const container = this.config.containerElement ?? document.body;
			requestAnimationFrame(() => {
				if (this.alertElement) try {
					container.appendChild(this.alertElement);
					this.logger.debug("AlertManager.createNewAlertElement: New alert element appended to %s.", container === document.body ? "document body" : "configured container");
					this.dispatchInitialTimerEvent(this.alertElement);
				} catch (appendError) {
					this.logger.error("AlertManager.createNewAlertElement: Failed to append alert element to %s:", container === document.body ? "document body" : "configured container", appendError);
					this.alertElement = null;
				}
			});
		} catch (creationError) {
			this.logger.error("AlertManager.createNewAlertElement: Failed to create/configure new alert for %s:", status, creationError);
			this.hideAlert();
		}
	}
	async configureAlertElement(element, status, statusResponse) {
		this.logger.debug(`AlertManager: Configuring alert element for status: ${status}`);
		try {
			const details = await this.getStatusDetails(status);
			const closeLabel = await this.i18nProvider.getUIString("close");
			if (statusResponse?.incident) details.incidents = {
				names: statusResponse.incident.names,
				statuses: statusResponse.incident.statuses,
				links: statusResponse.incident.links
			};
			this.setAlertAttributes(element, status, details, closeLabel);
			this.updateAlertTimerProgress(element);
			element.removeEventListener("oops-widget-closed", this.boundInternalAlertClosedHandler);
			element.addEventListener("oops-widget-closed", this.boundInternalAlertClosedHandler);
			this.logger.debug("AlertManager._configureAlertElement: Close listener configured.");
		} catch (error) {
			this.logger.error(`AlertManager: Failed to configure alert element for status ${status}:`, error);
			throw error;
		}
	}
	setAlertAttributes(element, status, statusDetails, closeButtonLabel) {
		const attributes = {
			"status-type": status,
			title: statusDetails.title,
			message: statusDetails.message,
			"incident-names": statusDetails.incidents?.names?.length ? JSON.stringify(statusDetails.incidents.names) : void 0,
			"incident-statuses": statusDetails.incidents?.statuses?.length ? JSON.stringify(statusDetails.incidents.statuses) : void 0,
			"incident-links": statusDetails.incidents?.links?.length ? JSON.stringify(statusDetails.incidents.links) : void 0,
			placement: this.config.placement,
			"mobile-enabled": this.config.mobile?.enabled ? "true" : void 0,
			"mobile-placement": this.config.mobile?.enabled ? this.config.mobile.placement : void 0,
			"mobile-media-query": this.config.mobile?.enabled ? this.config.mobile.mediaQuery : void 0,
			"display-incident-details": this.config.displayIncidentDetails ? "true" : void 0,
			"close-button-label": closeButtonLabel
		};
		Object.entries(attributes).forEach(([key, value]) => {
			if (value !== void 0) element.setAttribute(key, value);
			else element.removeAttribute(key);
		});
	}
	updateAlertTimerProgress(element = this.alertElement) {
		if (!element) return;
		let currentDuration = null;
		if (this.deps.timerManager) currentDuration = this.deps.timerManager.getCurrentTimerDuration();
		if (currentDuration !== null) {
			this.logger.debug(`AlertManager: Setting/Updating progress duration: ${currentDuration}ms`);
			element.setAttribute("progress-duration", currentDuration.toString());
		} else {
			this.logger.debug("AlertManager: Removing progress duration attribute.");
			element.removeAttribute("progress-duration");
		}
	}
	updateTimerProgressOnAlert() {
		if (this.alertElement) this.updateAlertTimerProgress(this.alertElement);
	}
	dispatchInitialTimerEvent(element = this.alertElement) {
		if (!element || !this.deps.timerManager) return;
		const initialDuration = this.deps.timerManager.getCurrentTimerDuration();
		if (initialDuration !== null) {
			this.logger.debug(`AlertManager: Dispatching initial timer start event with duration: ${initialDuration}ms`);
			element.dispatchEvent(new CustomEvent("oops-widget-timer-start", { detail: { duration: initialDuration } }));
		}
	}
	hideAlert() {
		if (!this.alertElement) return;
		const elementToRemove = this.alertElement;
		this.alertElement = null;
		this.logger.debug("AlertManager: Hiding alert");
		requestAnimationFrame(() => {
			if (elementToRemove?.parentNode) try {
				elementToRemove.removeEventListener("oops-widget-closed", this.boundInternalAlertClosedHandler);
				elementToRemove.parentNode.removeChild(elementToRemove);
				this.logger.debug("AlertManager: Alert element removed from DOM.");
			} catch (removeError) {
				this.logger.warn("AlertManager: Problem removing alert element:", removeError);
			}
			else this.logger.debug("AlertManager: Alert element already removed or had no parent.");
		});
	}
	refreshTimerProgressDisplay() {
		if (this.alertElement) this.updateAlertTimerProgress(this.alertElement);
	}
};

//#endregion
//#region src/managers/timerManager.ts
/** Enum defining the possible states of the TimerManager */
var TimerState = /* @__PURE__ */ function(TimerState$1) {
	TimerState$1["IDLE"] = "IDLE";
	TimerState$1["PERIODIC"] = "PERIODIC";
	TimerState$1["CHECKING"] = "CHECKING";
	return TimerState$1;
}(TimerState || {});
/**
* Manages periodic execution of an async task (onTick) with dynamically determined intervals.
*/
var TimerManager = class {
	currentState = TimerState.IDLE;
	pendingCheckTimeoutId = null;
	currentTimeoutInterval = null;
	onTick;
	getInterval;
	onTimerStart;
	onTimerCancel;
	logger = getLogger();
	constructor(options) {
		this.onTick = options.onTick;
		this.getInterval = options.getInterval;
		this.onTimerStart = options.onTimerStart;
		this.onTimerCancel = options.onTimerCancel;
	}
	/** Transition to a new state, logging the change */
	transitionState(newState) {
		if (this.currentState !== newState) {
			this.logger.debug(`TimerManager state transition: ${this.currentState} -> ${newState}`);
			this.currentState = newState;
		}
	}
	/** Clear any scheduled timeout */
	clearPendingCheck() {
		if (this.pendingCheckTimeoutId) {
			WINDOW.clearTimeout(this.pendingCheckTimeoutId);
			this.pendingCheckTimeoutId = null;
			this.currentTimeoutInterval = null;
		}
	}
	/**
	* Starts the periodic execution. Gets the initial interval and schedules the first tick.
	* If already running, it will effectively reschedule based on the current interval.
	*/
	start() {
		this.logger.debug("TimerManager start() called.");
		this.cancelCheck();
		const initialInterval = this.getInterval();
		this.logger.debug(`Obtained initial interval: ${initialInterval}ms`);
		if (initialInterval <= 0) {
			this.logger.debug("Initial interval is 0 or less, remaining IDLE.");
			this.transitionState(TimerState.IDLE);
			this.onTimerCancel?.();
			return;
		}
		this.scheduleNextTick(initialInterval);
	}
	/** Schedules the next execution of _runTick */
	scheduleNextTick(interval) {
		this.clearPendingCheck();
		if (interval <= 0) {
			this.logger.debug("Interval is 0 or less, stopping timer.");
			this.transitionState(TimerState.IDLE);
			this.onTimerCancel?.();
			return;
		}
		this.transitionState(TimerState.PERIODIC);
		this.currentTimeoutInterval = interval;
		this.pendingCheckTimeoutId = WINDOW.setTimeout(() => this._runTick(), interval);
		this.logger.debug(`Scheduled next tick in ${interval}ms`);
		this.onTimerStart?.(interval);
	}
	/** Internal method that runs the tick and schedules the next one */
	async _runTick() {
		if (this.currentState !== TimerState.PERIODIC) {
			this.logger.debug("_runTick was invoked but state is not PERIODIC, aborting tick cycle.", { currentState: this.currentState });
			return;
		}
		this.pendingCheckTimeoutId = null;
		this.transitionState(TimerState.CHECKING);
		this.logger.debug("Executing onTick...");
		try {
			await this.onTick();
			this.logger.debug("onTick execution completed.");
		} catch (error) {
			this.logger.error("Error during onTick execution:", error);
		}
		if (this.currentState === TimerState.CHECKING) {
			this.logger.debug("_runTick: onTick completed and state is still CHECKING. Proceeding to get next interval.");
			const newInterval = this.getInterval();
			this.logger.debug(`_runTick: Obtained next interval: ${newInterval}ms.`);
			this.scheduleNextTick(newInterval);
		} else this.logger.debug(`_runTick: onTick completed, and currentState is now ${this.currentState}. Assuming rescheduling/cancellation was handled within onTick. No further action here.`);
	}
	/** Get the duration of the currently scheduled timer, if any */
	getCurrentTimerDuration() {
		return this.currentState === TimerState.PERIODIC ? this.currentTimeoutInterval : null;
	}
	/** Cancels any pending check timer and transitions to IDLE state. */
	cancelCheck() {
		const wasActive = this.currentState !== TimerState.IDLE;
		this.logger.debug(`cancelCheck() called. Was active: ${wasActive}`);
		this.clearPendingCheck();
		this.transitionState(TimerState.IDLE);
		if (wasActive) this.onTimerCancel?.();
	}
	/** Cleans up resources. */
	destroy() {
		this.logger.debug("Destroying timer manager.");
		this.cancelCheck();
		this.onTick = void 0;
		this.getInterval = void 0;
		this.onTimerStart = void 0;
		this.onTimerCancel = void 0;
	}
};

//#endregion
//#region src/stateMachine.ts
var WidgetStateMachine = class {
	state = WidgetState.IDLE;
	status = SystemStatus.Operational;
	closeTimeoutId = null;
	config;
	logger = getLogger();
	shouldShowAlert;
	statusCheckFn;
	listeners = [];
	constructor(options) {
		this.config = options.config;
		if (!options.shouldShowAlert) {
			this.logger.error("Alert storage functions were not provided to StateMachine!");
			this.shouldShowAlert = () => true;
		} else this.shouldShowAlert = options.shouldShowAlert;
		this.statusCheckFn = options.statusCheckFn || (async () => {
			this.logger.warn("No status check function provided to StateMachine");
			return {
				status: SystemStatus.Operational,
				rawData: { s: SystemStatus.Operational }
			};
		});
	}
	/**
	* Subscribe to state machine events
	* @returns Unsubscribe function
	*/
	subscribe(listener) {
		this.listeners.push(listener);
		return () => {
			this.listeners = this.listeners.filter((l) => l !== listener);
		};
	}
	/**
	* Emit an event to all listeners
	*/
	emit(event) {
		this.logger.debug("Emitting event", event);
		this.listeners.forEach((listener) => listener(event));
	}
	/**
	* Start the state machine with the initial status
	*/
	start(initialStatus, response) {
		this.logger.debug("start() called", {
			initialStatus,
			response
		});
		if (response?.degraded) {
			this.logger.warn("Starting with degraded status response:", {
				status: initialStatus,
				errorDetails: response.errorDetails
			});
			this.emit({
				type: EventType.STATE_CHANGED,
				payload: {
					state: this.getInitialState(initialStatus),
					status: initialStatus,
					response,
					degraded: true
				}
			});
		}
		this.transitionTo(this.getInitialState(initialStatus), initialStatus, response);
	}
	/**
	* Handle a new status update
	*/
	updateStatus(newStatus, response) {
		this.logger.debug("updateStatus() called", {
			newStatus,
			response,
			currentStatus: this.status,
			currentState: this.state
		});
		if (newStatus !== this.status || this.state === WidgetState.IDLE) this.transitionTo(this.getNextState(newStatus), newStatus, response);
		else this.logger.debug("Status update received, but no state change needed.");
	}
	/**
	* Handle user closing the widget
	*/
	closeAlert() {
		this.logger.debug("closeAlert() called", {
			currentStatus: this.status,
			currentState: this.state
		});
		this.emit({
			type: EventType.CLOSE_ALERT,
			payload: {
				status: this.status,
				state: this.state
			}
		});
		this.transitionTo(WidgetState.CLOSED, this.status);
	}
	/**
	* Internal: transition logic
	*/
	transitionTo(newState, status, response) {
		this.logger.debug("Transition", {
			fromState: this.state,
			toState: newState,
			fromStatus: this.status,
			toStatus: status,
			degraded: response?.degraded
		});
		this.clearTimers();
		this.state = newState;
		this.status = status;
		this.emit({
			type: EventType.STATE_CHANGED,
			payload: {
				state: newState,
				status,
				response,
				degraded: response?.degraded
			}
		});
		switch (newState) {
			case WidgetState.VISIBLE:
				this.logger.debug(`Entering VISIBLE state`, {
					status,
					degraded: response?.degraded
				});
				this.emit({
					type: EventType.SHOW_ALERT,
					payload: {
						status,
						response,
						degraded: response?.degraded
					}
				});
				break;
			case WidgetState.CLOSED:
				this.logger.debug(`Entering CLOSED state`, { status });
				this.emit({ type: EventType.HIDE_ALERT });
				this.scheduleCloseTimeout();
				break;
			case WidgetState.IDLE:
				this.logger.debug("Entering IDLE state", { status });
				this.emit({ type: EventType.HIDE_ALERT });
				break;
		}
	}
	/**
	* Determine initial state based on status and closed alert data
	*/
	getInitialState(status) {
		const closeDurationMs = this.getCloseDurationForStatus(status);
		if (status === SystemStatus.Operational) return WidgetState.IDLE;
		if (!this.shouldShowAlert(status, closeDurationMs)) {
			this.logger.debug("Initial state determined as CLOSED due to recent closure.");
			return WidgetState.CLOSED;
		}
		if (status === SystemStatus.UnderMaintenance) return WidgetState.VISIBLE;
		return WidgetState.VISIBLE;
	}
	/**
	* Determine next state based on status and current state
	*/
	getNextState(status) {
		if (status === SystemStatus.Operational) return WidgetState.IDLE;
		return WidgetState.VISIBLE;
	}
	/**
	* Schedule close timeout (for CLOSED state)
	*/
	scheduleCloseTimeout() {
		if (this.status === SystemStatus.UnderMaintenance) {
			this.logger.debug("Status is UnderMaintenance. Alert will remain closed indefinitely (suppressed). No timeout scheduled.");
			this.emit({ type: EventType.HIDE_ALERT });
			return;
		}
		const closeDuration = this.getCloseDuration();
		this.logger.debug("scheduleCloseTimeout() for non-maintenance status", { closeDuration });
		if (closeDuration > 0) this.closeTimeoutId = WINDOW.setTimeout(() => {
			this.logger.debug("closeTimeout expired, reopening alert if still CLOSED", {
				state: this.state,
				status: this.status
			});
			if (this.state === WidgetState.CLOSED) {
				this.logger.debug("Close timeout finished and state is still CLOSED, transitioning to VISIBLE.");
				this.transitionTo(WidgetState.VISIBLE, this.status);
			}
		}, closeDuration);
		else {
			this.logger.debug(`Close duration is 0 for status ${this.status}. Alert will remain closed indefinitely (suppressed).`);
			this.emit({ type: EventType.HIDE_ALERT });
		}
	}
	/**
	* Get close duration for current status
	*/
	getCloseDuration() {
		return this.getCloseDurationForStatus(this.status);
	}
	/**
	* Helper to get close duration for a specific status
	*/
	getCloseDurationForStatus(status) {
		return getCloseDurationMs(this.config, status);
	}
	/**
	* Get check interval for current status, enforcing a minimum of 30 seconds.
	*/
	getCheckInterval() {
		return getValidatedCheckIntervalMs(this.config, this.status, this.logger);
	}
	/**
	* Public method to trigger a status check
	*/
	async triggerCheck() {
		this.logger.debug("triggerCheck() called");
		this.emit({ type: EventType.STATUS_CHECK_REQUESTED });
		try {
			const response = await this.statusCheckFn();
			this.emit({
				type: EventType.STATUS_CHECK_COMPLETED,
				payload: {
					status: response.status,
					response,
					degraded: response.degraded
				}
			});
			if (response.degraded) {
				this.logger.warn("Received degraded status response:", {
					status: response.status,
					errorDetails: response.errorDetails
				});
				if (response.status === SystemStatus.Operational && this.state === WidgetState.VISIBLE && this.status !== SystemStatus.Operational) {
					this.logger.info("Keeping non-operational alert visible despite degraded Operational status");
					this.emit({
						type: EventType.STATE_CHANGED,
						payload: {
							state: this.state,
							status: this.status,
							response,
							degraded: true
						}
					});
					return response;
				}
				if (this.state === WidgetState.VISIBLE) this.emit({
					type: EventType.STATE_CHANGED,
					payload: {
						state: this.state,
						status: this.status,
						response,
						degraded: true
					}
				});
				return response;
			}
			this.updateStatus(response.status, response);
			return response;
		} catch (error) {
			this.logger.error("Error during status check:", error);
			throw error;
		}
	}
	/**
	* Clear all timers
	*/
	clearTimers() {
		if (this.closeTimeoutId) {
			WINDOW.clearTimeout(this.closeTimeoutId);
			this.closeTimeoutId = null;
		}
	}
	/**
	* Destroy the state machine and clear timers
	*/
	destroy() {
		this.clearTimers();
		this.listeners = [];
	}
	/**
	* Get the current status maintained by the state machine.
	*/
	get currentStatus() {
		return this.status;
	}
	/**
	* Get the current state of the widget.
	*/
	get currentState() {
		return this.state;
	}
	/**
	* Trigger a status check in response to an interceptor error.
	* This is separate from the regular triggerCheck to properly handle
	* the special case of interceptor-detected errors.
	*
	* @returns Promise that resolves to the system status response
	*/
	triggerErrorCheck() {
		this.logger.debug("triggerErrorCheck() called due to interceptor error");
		this.emit({
			type: EventType.STATE_CHANGED,
			payload: {
				state: this.state,
				status: this.status,
				degraded: true
			}
		});
		this.emit({ type: EventType.STATUS_CHECK_REQUESTED });
		return this.triggerCheck().finally(() => {
			this.emit({
				type: EventType.STATE_CHANGED,
				payload: {
					state: this.state,
					status: this.status
				}
			});
		});
	}
};

//#endregion
//#region src/core.ts
/**
* OopsWidget class that handles the main functionality of the library
*/
var OopsWidget = class {
	config;
	_fetch = async () => new Response("");
	cleanupFunctions = [];
	logger = getLogger();
	i18nProvider;
	errorManager = getErrorManager();
	stateMachine;
	timerManager;
	alertManager;
	stateEventUnsub;
	lastKnownStatus;
	/**
	* Create a new OopsWidget instance
	* @param config Configuration options
	*/
	constructor(config) {
		this.config = {
			...DEFAULT_CONFIG,
			...config
		};
		this.cleanupFunctions = [];
		this.logger.configure(this.config);
		this.logger.debug("OopsWidget initializing with config:", this.config);
		try {
			this._setupDependencies();
			this._setupFetch();
			this._setupDOM();
			this._setupInterceptors();
			this._setupManagersAndStateMachine();
			this._setupAlertManager();
		} catch (globalError) {
			this.logger.error("Error during initialization:", globalError);
		}
	}
	/** Setup core dependencies like i18nProvider */
	_setupDependencies() {
		if (!this.config.i18nProvider) throw new Error("Provide an i18nProvider in the config to use the OopsWidget. If you want to use the default i18nProvider, please import it from \"@upreport/oops-widget/i18n\".");
		this.i18nProvider = this.config.i18nProvider;
	}
	/** Setup the sandboxed fetch instance */
	_setupFetch() {
		if (this.config.useSandboxedFetch) {
			this.logger.debug("Using sandboxed fetch.");
			try {
				this._fetch = createSandboxedFetch();
			} catch (fetchError) {
				this.logger.warn("Failed to create sandboxed fetch, using fallback:", fetchError);
				this._fetch = async () => {
					return new Response(`s=${SystemStatus.Operational}`, {
						status: 200,
						headers: { "Content-Type": "text/plain" }
					});
				};
			}
		} else {
			this.logger.debug("Using native fetch as per configuration.");
			this._fetch = WINDOW.fetch.bind(WINDOW);
		}
	}
	/** Register the custom element for the alert */
	_setupDOM() {
		try {
			const alertTagName = "oops-widget";
			if (!customElements.get(alertTagName)) if (this.config.alertComponent) try {
				customElements.define(alertTagName, this.config.alertComponent);
			} catch (defineError) {
				this.logger.warn("Failed to define custom element:", defineError);
			}
			else {
				this.logger.error("alertComponent is missing in the configuration. Cannot register the custom element.");
				throw new Error("OopsWidget requires alertComponent in the configuration.");
			}
			else this.logger.debug("Custom element oops-widget already registered.");
		} catch (customElementsError) {
			this.logger.warn("Failed during custom element setup:", customElementsError);
		}
	}
	/** Setup interceptors if enabled */
	_setupInterceptors() {
		if (this.config.interceptors && this.config.interceptors.length > 0) try {
			for (const interceptor of this.config.interceptors) try {
				const cleanup = interceptor.setup();
				if (typeof cleanup === "function") this.cleanupFunctions.push(cleanup);
				else this.logger.warn(`${interceptor.name} interceptor setup did not return a cleanup function.`);
			} catch (interceptorSetupError) {
				this.logger.warn(`Failed to set up ${interceptor.name} interceptor:`, interceptorSetupError);
			}
		} catch (customInterceptorError) {
			this.logger.warn("Failed to iterate or set up custom interceptors:", customInterceptorError);
		}
	}
	/** Instantiate and connect Managers and State Machine */
	_setupManagersAndStateMachine() {
		this.timerManager = new TimerManager({
			onTick: async () => {
				try {
					await this.stateMachine?.triggerCheck();
				} catch (error) {
					this.logger.error("Error during timer tick (triggerCheck):", error);
				}
			},
			getInterval: () => {
				return this.stateMachine?.getCheckInterval() ?? 0;
			},
			onTimerStart: (duration) => {
				this.logger.debug(`Timer started event received by OopsWidget, duration: ${duration}ms`);
				this.alertManager?.notifyTimerStarted(duration);
			},
			onTimerCancel: () => {
				this.logger.debug("Timer cancelled event received by OopsWidget.");
				this.alertManager?.notifyTimerCancelled();
			}
		});
		this.stateMachine = new WidgetStateMachine({
			config: this.config,
			statusCheckFn: async () => this._internalCheckStatus(),
			shouldShowAlert
		});
		this.errorManager.setStatusCheckTrigger(() => {
			this.stateMachine?.triggerErrorCheck();
		});
		this.stateEventUnsub = this.stateMachine.subscribe(this.handleStateMachineEvent.bind(this));
	}
	_setupAlertManager() {
		if (!this.timerManager) {
			this.logger.error("Cannot setup AlertManager: TimerManager is not initialized.");
			return;
		}
		if (!this.i18nProvider) {
			this.logger.error("Cannot setup AlertManager: i18nProvider is not initialized.");
			return;
		}
		this.alertManager = new AlertManager({
			config: this.config,
			logger: this.logger,
			i18nProvider: this.i18nProvider,
			timerManager: this.timerManager,
			internalCheckStatusFn: this._internalCheckStatus.bind(this),
			notifyAlertClosedFn: this._handleAlertClosedByUIManager.bind(this)
		});
		this.logger.debug("AlertManager initialized.");
	}
	/**
	* Handle events from the state machine
	*/
	handleStateMachineEvent(event) {
		switch (event.type) {
			case EventType.SHOW_ALERT:
				if (event.payload && this.alertManager) {
					const showAlertPayload = event.payload;
					if (showAlertPayload.status) this.alertManager.showAlert(showAlertPayload.status, showAlertPayload.response);
				}
				break;
			case EventType.HIDE_ALERT:
				this.alertManager?.hideAlert();
				break;
			case EventType.CLOSE_ALERT:
				if (event.payload) {
					const closeAlertPayload = event.payload;
					if (closeAlertPayload.status) recordClosedAlert(closeAlertPayload.status);
				}
				break;
			case EventType.STATE_CHANGED:
				if (event.payload) {
					const stateChangedPayload = event.payload;
					this.logger.debug("Received state change event:", {
						newState: stateChangedPayload.state,
						newStatus: stateChangedPayload.status,
						degraded: stateChangedPayload.degraded
					});
					if (this.timerManager) {
						const interval = this.stateMachine?.getCheckInterval() ?? 0;
						this.logger.debug(`State changed. New check interval from state machine: ${interval}ms. Restarting timer.`);
						this.timerManager.start();
					}
					this.alertManager?.refreshTimerProgressDisplay();
				}
				break;
		}
	}
	/**
	* Internal implementation of status check with AbortController
	*/
	async _internalCheckStatus() {
		this.logger.debug("Checking system status...");
		const controller = new AbortController();
		const signal = controller.signal;
		const timeoutDuration = this.config.statusCheckTimeoutMs;
		let timeoutId;
		let timedOut = false;
		timeoutId = WINDOW.setTimeout(() => {
			timedOut = true;
			controller.abort();
		}, timeoutDuration);
		try {
			const response = await this._fetch(this.config.statusUrl, {
				cache: "no-cache",
				signal
			});
			if (timeoutId) {
				WINDOW.clearTimeout(timeoutId);
				timeoutId = void 0;
			}
			if (!response.ok) {
				this.logger.error(`Status check failed with HTTP status: ${response.status}`);
				return {
					status: SystemStatus.Operational,
					rawData: {}
				};
			}
			const text = await response.text();
			let parsedData;
			try {
				parsedData = parseAdvancedUrlParams(text);
			} catch {
				this.logger.error("Cannot parse status response, treating as degraded");
				return {
					status: this.lastKnownStatus ?? SystemStatus.Operational,
					degraded: true,
					errorDetails: "parse_error",
					rawData: {}
				};
			}
			const status = parsedData.s && Object.values(SystemStatus).includes(parsedData.s) ? parsedData.s : SystemStatus.Operational;
			this.lastKnownStatus = status;
			let incident;
			if (status !== SystemStatus.Operational) {
				incident = {
					names: parsedData.incident_names,
					statuses: parsedData.incident_statuses,
					links: parsedData.incident_links
				};
				if ((!incident.names || incident.names.length === 0) && (!incident.statuses || incident.statuses.length === 0) && (!incident.links || incident.links.length === 0)) incident = void 0;
			}
			const statusResponse = {
				status,
				incident,
				rawData: parsedData
			};
			this.logger.debug("System status check successful:", statusResponse);
			return statusResponse;
		} catch (err) {
			if (timeoutId) {
				WINDOW.clearTimeout(timeoutId);
				timeoutId = void 0;
			}
			if (signal.aborted && timedOut) this.logger.warn(`Status check timed-out after ${timeoutDuration} ms`);
			else {
				const msg = err instanceof Error ? err.message : String(err);
				this.logger.error(`Fetch error during status check: ${msg}`, err);
			}
			const cachedStatus = this.lastKnownStatus ?? SystemStatus.Operational;
			const errorDetails = err instanceof Error ? err.message : String(err);
			if (!this.lastKnownStatus) this.logger.warn("No previous status available, using Operational as fallback with degraded flag.", { error: errorDetails });
			else this.logger.warn(`Using last known status: ${this.lastKnownStatus} in degraded mode.`, { error: errorDetails });
			return {
				status: cachedStatus,
				degraded: true,
				errorDetails,
				rawData: {}
			};
		}
	}
	/**
	* Destroy the widget and clean up all resources
	*/
	destroy() {
		try {
			this.logger.debug("Destroying widget...");
			this.alertManager?.hideAlert();
			if (this.stateEventUnsub) {
				this.stateEventUnsub();
				this.stateEventUnsub = void 0;
			}
			if (this.timerManager) {
				this.timerManager.destroy();
				this.timerManager = void 0;
			}
			if (this.stateMachine) {
				this.stateMachine.destroy();
				this.stateMachine = void 0;
			}
			clearAlertStorage();
			for (const cleanup of this.cleanupFunctions) try {
				cleanup();
			} catch (cleanupError) {
				this.logger.warn("Error during interceptor cleanup function execution:", cleanupError);
			}
			this.cleanupFunctions = [];
			this.alertManager = void 0;
			this.logger.info("OopsWidget destroyed successfully.");
		} catch (globalError) {
			this.logger.error("Global error during widget destruction:", globalError);
			this.stateEventUnsub = void 0;
			this.timerManager = void 0;
			this.stateMachine = void 0;
			this.cleanupFunctions = [];
		}
	}
	/**
	* Notify the state machine that the user has closed the alert.
	* This method is called by AlertManager via the notifyAlertClosedFn callback.
	*/
	_handleAlertClosedByUIManager() {
		this.logger.debug("OopsWidget: AlertManager reported user closed alert, notifying state machine.");
		this.stateMachine?.closeAlert();
	}
	/**
	* Start the widget: performs an initial status check and starts the internal state machine.
	*/
	async start() {
		this.logger.debug("Starting widget...");
		let initialResponse;
		try {
			if (!this.alertManager) {
				this.logger.warn("AlertManager not set up during start, attempting to set up now.");
				if (!this.timerManager || !this.i18nProvider) this._setupDependencies();
				this._setupAlertManager();
				if (!this.alertManager) throw new Error("Failed to initialize AlertManager during start.");
			}
			initialResponse = await this._internalCheckStatus();
			if (initialResponse.degraded && initialResponse.errorDetails) throw new Error(`Initial status check failed: ${initialResponse.errorDetails}`);
			if (!initialResponse.degraded && initialResponse.status) this.lastKnownStatus = initialResponse.status;
			if (!this.stateMachine) {
				this.logger.error("Cannot start widget: State machine is not initialized.");
				throw new Error("Widget initialization failed, cannot start.");
			}
			if (initialResponse.degraded) this.logger.warn("Widget starting in degraded mode with cached status:", initialResponse.status);
			this.stateMachine.start(initialResponse.status, initialResponse);
			this.logger.info("OopsWidget started successfully.");
			this.timerManager?.start();
		} catch (startError) {
			this.logger.error("Failed to start widget:", startError);
			throw startError;
		}
	}
};
/**
* Factory function to create a new OopsWidget instance
*/
function createOopsWidget(config) {
	if (!config?.statusUrl) throw new Error("OopsWidgetConfig requires a statusUrl.");
	if (!config?.alertComponent) throw new Error("OopsWidgetConfig requires an alertComponent.");
	if (!config?.i18nProvider) throw new Error("OopsWidgetConfig requires an i18nProvider.");
	return new OopsWidget(config);
}

//#endregion
export { LogLevel, SystemStatus, createOopsWidget };